import { motion } from 'motion/react';
import { ArrowRight, Shield, Award, Gem } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function LandingPage() {
  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=2000"
            alt="Minimalist Architecture"
            className="w-full h-full object-cover opacity-20 grayscale"
            referrerPolicy="no-referrer"
          />
        </div>
        <div className="relative z-10 text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="kicker">The Private Ecosystem</span>
            <h1 className="md:text-8xl leading-none">
              She Deserves Better
            </h1>
            <span className="text-[10px] uppercase tracking-[0.5em] text-gold mt-4 block mb-8 font-bold">Know Yourself. Choose Better.</span>
            <p className="text-taupe text-lg md:text-xl max-w-2xl mx-auto mb-12 font-light italic">
              The exclusive portal behind the global video essays. High-status relationship architecture for women who demand clinical precision and absolute excellence.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link to="/audit" className="btn-gold w-full sm:w-auto">
                Begin Audit
              </Link>
              <Link to="/philosophy" className="btn-outline w-full sm:w-auto">
                Explore Philosophy
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Philosophy Briefing */}
      <section className="bg-theme-bg border-y border-theme-border">
        <div className="section-container grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
            <div className="relative aspect-[4/5] overflow-hidden border border-taupe/20">
                <img
                    src="https://images.unsplash.com/photo-1518005020455-6893693fb166?auto=format&fit=crop&q=80&w=1000"
                    alt="Clinical Minimal"
                    className="w-full h-full object-cover grayscale"
                    referrerPolicy="no-referrer"
                />
            </div>
            <div className="flex flex-col">
                <h2 className="text-4xl font-serif mb-8 font-light italic">Clinical Psychology. <br/>Strategic Excellence.</h2>
                <div className="divider" />
                <p className="text-taupe mb-8 leading-relaxed">
                    Relationships are the most significant asset in a high-net-worth life. We move beyond generic dating advice and treat partnership selection with the same rigor, discretion, and architectural planning as a private equity acquisition.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mt-4">
                    <div className="flex flex-col space-y-3">
                        <Shield className="h-6 w-6 text-gold mb-2" />
                        <h4 className="text-xs font-bold tracking-widest">Confidentiality</h4>
                        <p className="text-xs text-taupe">Total discretion in all member interactions and data vaults.</p>
                    </div>
                    <div className="flex flex-col space-y-3">
                        <Award className="h-6 w-6 text-gold mb-2" />
                        <h4 className="text-xs font-bold tracking-widest">Prestige</h4>
                        <p className="text-xs text-taupe">Access to a curated circle of global vetted recommendations.</p>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* Feature Grid */}
      <section className="py-24 bg-theme-bg/50">
        <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
                <h2 className="text-3xl font-serif italic mb-4">The Ecosystem</h2>
                <p className="text-taupe uppercase text-[10px] tracking-[0.3em]">Architectural Pillars</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                {[
                    {
                        title: "The Trust Audit",
                        desc: "Identify structural weaknesses in your partner's reliability with clinical accuracy.",
                        icon: <Gem className="h-5 w-5" />,
                        to: "/audit"
                    },
                    {
                        title: "Vetted Affiliates",
                        desc: "Curated aesthetic and logistical recommendations for the high-end female lifestyle.",
                        icon: <Award className="h-5 w-5" />,
                        to: "/curation"
                    },
                    {
                        title: "Video Archive",
                        desc: "Exclusive frameworks, essay transcripts, and briefing papers for members.",
                        icon: <Shield className="h-5 w-5" />,
                        to: "/library"
                    }
                ].map((item, i) => (
                    <motion.div
                        key={i}
                        whileHover={{ y: -10 }}
                        className="p-8 border border-theme-border bg-theme-bg transition-colors hover:border-gold/30 flex flex-col"
                    >
                        <div className="text-gold mb-6">{item.icon}</div>
                        <h3 className="text-lg font-serif mb-4 italic">{item.title}</h3>
                        <p className="text-sm text-taupe mb-8 leading-relaxed">{item.desc}</p>
                        <Link to={item.to} className="mt-auto flex items-center text-[10px] uppercase tracking-widest text-gold hover:text-theme-text transition-colors">
                            Explore <ArrowRight className="ml-2 h-3 w-3" />
                        </Link>
                    </motion.div>
                ))}
            </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gold/5 text-center">
          <div className="max-w-2xl mx-auto px-6">
              <h2 className="text-3xl md:text-5xl font-serif mb-8 leading-tight italic">Elevate Your Standard.</h2>
              <p className="text-taupe mb-12 italic">Join the global directory of high-status women redefining the architectural blueprint of power and partnership.</p>
              <Link to="/audit" className="btn-gold inline-block">Join the Vanguard</Link>
          </div>
      </section>
    </div>
  );
}
